// node_modules
import {
    FC,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import Modal from "react-modal";
// Custom hooks
import { useFetch } from "Hooks";
// Stores
import { CaseContext, AuthContext } from "Stores";
// Types
import { TTechnologyImageDTO, TTechnologyMediaDTO, TTechnologyYoutubeVideoDTO, TUseFetch } from "Types";
// Shared
import {
    AddMediaModal,
    AsynchronousWrapper,
    SidebarMediaPreviewFilterMenu
} from "Components";
import { MediaPreviewTypedChild } from "Components/Shared/TypedChildren/MediaPreviewTypedChild";
import { YoutubeVideo } from "./YoutubeVideo";
import { Image } from "./Image";
// styles
import styles from "./technologyMedia.module.scss";
import { PendoHelperSingleton } from "Helpers";
import { LoggingConstants } from "Constants";

export const TechnologyMedia: FC = () => {
    // Shared states
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);

    const [defaultSelectedMedia, setDefaultSelectedMedia] = useState("");
    const [isAddMediaModalOpen, setIsAddMediaModalOpen] = useState(false);
    const [images, setImages] = useState<TTechnologyImageDTO[]>([]);
    const [youtubeVideos, setYoutubeVideos] = useState<TTechnologyYoutubeVideoDTO[]>([]);

    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TTechnologyMediaDTO> = useFetch(`api/technologies/${caseState.selectedTechnologyId}/medias`);

    // We build image components list
    const getImageElements = () => {
        const imageElements = images.map((image, index) => {
            return (
                <MediaPreviewTypedChild
                    key={`i-${image.id}`}
                    previewType="Figures"
                    previewName={`image-${index}`}
                    previewLink={image.link}>
                    <Image isExpandable={true} id={image.id} caption={image.caption} link={image.link} technologyId={caseState.selectedTechnologyId} />
                </MediaPreviewTypedChild>
            );
        });
        return imageElements;
    };

    // We build youtube video components list
    const getYoutubeVideoElements = () => {
        const youtubeVideoElements = youtubeVideos.map((video, index) => {
            return (
                <MediaPreviewTypedChild
                    key={`ytb-${video.id}`}
                    previewType="Videos"
                    previewName={`youtubeVideo-${index}`}
                    previewLink={video.previewLink}>
                    <YoutubeVideo caption={video.caption} link={video.link} />
                </MediaPreviewTypedChild>
            );
        });
        return youtubeVideoElements;
    };

    const getAllMediaElements = () => {
        // We merge lists
        const medias = [];
        const imageElements = getImageElements();
        if (imageElements) medias.push(...imageElements);
        const youtubeVideoElements = getYoutubeVideoElements();
        if (youtubeVideoElements) medias.push(...youtubeVideoElements);
        return medias;
    };

    // We get the default selected media
    const getDefaultSelectedMedia = useCallback((): string => {
        if (fetchedData && fetchedData.images.length >= 1) return "image-0";
        if (fetchedData && fetchedData.youtubeVideos.length >= 1) return "youtubeVideo-0";
        return "";
    }, [fetchedData]);

    const startAddingNewMediaType = (previewType: string) => {
        previewType = previewType.toLowerCase();
        setIsAddMediaModalOpen(true);
    };

    const addImage = (image: TTechnologyImageDTO) => {
        setImages(images.concat(image));
        setIsAddMediaModalOpen(false);
        setDefaultSelectedMedia(`image-${images.length}`);
    };

    const addYoutubeVideo = (video: TTechnologyYoutubeVideoDTO) => {
        setYoutubeVideos(youtubeVideos.concat(video));
        setIsAddMediaModalOpen(false);
        setDefaultSelectedMedia(`youtubeVideo-${youtubeVideos.length}`);
    };

    useEffect(() => {
        if (fetchedData) {
            setImages(fetchedData.images);
            setYoutubeVideos(fetchedData.youtubeVideos);
            setDefaultSelectedMedia(getDefaultSelectedMedia());

            // Log the request for medias
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetMedias`, {
                caseId: caseState.selectedCaseId
            });
        }
    }, [getDefaultSelectedMedia, fetchedData, caseState.selectedCaseId]);

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            {fetchedData && (fetchedData.images.length >= 1 || fetchedData.youtubeVideos.length >= 1 || (authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery)) ?
                <>
                    <Modal
                        isOpen={isAddMediaModalOpen}
                        onRequestClose={() => { setIsAddMediaModalOpen(false); }}
                        className={styles.mediaModalContent}
                        overlayClassName={styles.mediaModalOverlay}
                    >
                        <AddMediaModal technologyId={caseState.selectedTechnologyId} addImage={addImage} addYoutubeVideo={addYoutubeVideo} />
                    </Modal>
                    <SidebarMediaPreviewFilterMenu defaultSelectedPreviewName={defaultSelectedMedia} addElement={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ? startAddingNewMediaType : undefined}>
                        {getAllMediaElements()}
                    </SidebarMediaPreviewFilterMenu>
                </>
                :
                <></>
            }
        </AsynchronousWrapper>
    );
};
